import { FC, SyntheticEvent, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Highcharts from 'highcharts';
import merge from 'lodash/merge';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { formatBillingDate } from 'components/billingAndUsage/utils';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';
import { getBaseGraphConfig } from 'components/common/Graphs/graphConfigs';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

import { usageGraphStylesState } from 'styles/stylesProvider';
import './UsageGraph.scss';

type UsageGraphProps = {
  xAxis: Array<number>;
  yAxis: Array<number>;
  usagePerDay: number;
  overallVolume: number;
};

export const UsageGraph: FC<UsageGraphProps> = ({ xAxis, yAxis, usagePerDay, overallVolume }: UsageGraphProps) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const maxHeight = Math.max(...yAxis, usagePerDay);
  const usageGraphStyles = useRecoilValue(usageGraphStylesState);
  const timezone = useRecoilValue(timezoneState);

  function renderSelectedStatistics(): JSX.Element | null {
    if (hoverIndex === null) return null;

    const volume = yAxis[hoverIndex];
    const date = formatBillingDate(xAxis[hoverIndex], timezone);

    return (
      <>
        <div>
          <span>{volume}</span>
        </div>
        <div>GB/DAY</div>
        <div>{date}</div>
      </>
    );
  }

  const seriesBarData: Array<{ y: number; color: string }> = [];
  const seriesLineData: Array<number> = [];
  const seriesBackgroundData = yAxis.map((item) => {
    seriesLineData.push(usagePerDay);
    if (item < usagePerDay) {
      seriesBarData.push({
        color: usageGraphStyles.primaryColor,
        y: item,
      });
    } else {
      seriesBarData.push({
        color: usageGraphStyles.thresholdColor,
        y: item,
      });
    }
    if (item < maxHeight) {
      return maxHeight - item;
    }
    return 0;
  });

  function resetActive(): void {
    setHoverIndex(null);
  }

  function renderAverageStatistics(): JSX.Element {
    const volume = overallVolume ?? 0;
    return (
      <>
        <div>
          <span>{volume}</span>
        </div>
        <div>GB/DAY</div>
        <div>(AVERAGE)</div>
      </>
    );
  }

  function handleMouseOver(e: SyntheticEvent): void {
    // @ts-expect-error
    setHoverIndex(e?.target?.index);
  }

  function handleMouseOut(): void {
    resetActive();
  }

  const CHART = {
    height: 80,
    margin: [0, 0, 0, 0],
    styledMode: false,
    spacingTop: 0,
    spacingBottom: 0,
    spacingLeft: 0,
    spacingRight: 0,
  };

  const PLOT_OPTIONS = {
    plotOptions: {
      column: {
        colorByPoint: false,
        marker: {
          enabled: false,
        },
        lineWidth: 1.5,
      },
    },
    series: {
      minPointLength: 3,
    },
  };

  const X_AXIS = {
    labels: {
      enabled: false,
    },
    visible: false,
    categories: xAxis,
  };

  const Y_AXIS = {
    labels: {
      enabled: false,
    },
    visible: false,
    max: maxHeight,
    tickInterval: 1,
  };

  const SERIES = [
    {
      data: seriesBackgroundData,
      type: K.SERIES_TYPES.COLUMN,
      stacking: 'normal',
      color: 'transparent',
      borderWidth: 0,
      pointWidth: 20,
      point: {
        events: {
          mouseOver: handleMouseOver,
          mouseOut: handleMouseOut,
        },
      },
      states: {
        hover: {
          enabled: true,
          color: usageGraphStyles.backgroundColor,
        },
      },
      animation: false,
    },
    {
      data: seriesBarData,
      type: K.SERIES_TYPES.COLUMN,
      stacking: 'normal',
      color: 'transparent',
      pointWidth: 20,
      point: {
        events: {
          mouseOver: handleMouseOver,
          mouseOut: handleMouseOut,
        },
      },
      animation: false,
    },
    {
      data: seriesLineData,
      type: K.SERIES_TYPES.LINE,
      dashStyle: 'Dash',
      color: usageGraphStyles.thresholdColor,
      lineWidth: 1,
      allowPointSelect: false,
      stacking: 'normal',
      marker: {
        enabled: false,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
      animation: false,
    },
  ];

  const CONFIG = {
    chart: CHART,
    plotOptions: PLOT_OPTIONS,
    xAxis: X_AXIS,
    yAxis: Y_AXIS,
    series: SERIES,
  };

  const config = merge(getBaseGraphConfig(), CONFIG);

  return (
    <div className="UsageGraph__Container" data-test-id={ELEMENT.planPageUsageGraph}>
      <div className="Statistics__Container">
        <ScalyrHighcharts
          options={config}
          highcharts={Highcharts}
          containerProps={{ style: { height: '100%', width: '100%' } }}
        />
        <div className="Statistics__UsageOverview" data-test-id={ELEMENT.planPageUsageGraphOverview}>
          {hoverIndex !== null ? renderSelectedStatistics() : renderAverageStatistics()}
        </div>
      </div>
      <div className="UsageGraph__Footer">
        The dashed red line is your plan, and any red bars show which days you have exceeded the plan
      </div>
    </div>
  );
};
